import {
    Icon,
    Button,
    Form,
    FormGroup,
    FormField,
    Message,
    TextArea,
    Popup,
    Checkbox,
    Loader,
    Progress
} from 'semantic-ui-react'
import { useState, useEffect, useRef } from 'react'
import { IcctDatePicker } from '../common/IcctDatePicker'
import { LoadingWrapper } from '../common'
import { getUnresolvedProblems } from '../common/apiClient'
import { fetchWithAuth, getCSRFToken, useAuth } from '../auth'
import { Link, useParams } from 'react-router-dom'
import { getAsset } from '../common/apiClient'
import { State, City } from 'country-state-city'
import { AssetProblemAttachments } from './AssetProblemAttachments'
import {
    createPendingAttachmentItem,
    revokePendingAttachmentPreviews
} from './assetProblemAttachmentConfig'
import {
    deleteAttachment,
    uploadPendingAttachments as uploadPendingProblemAttachments
} from './assetProblemService'

export const AssetProblemIntakeForm = props => {
    const { id, id2 } = useParams()
    const auth = useAuth()
    const { isReadOnly } = auth
    const isNew = id2 === 'new'
    const [asset, setAsset] = useState({})
    const [success, setSuccess] = useState(false)
    const [error, setError] = useState(false)
    const [loaded, setLoaded] = useState(true)
    const [fieldErrors, setFieldErrors] = useState({})
    const [unresolvedProblems, setUnresolvedProblems] = useState(true)
    const states = State.getStatesOfCountry('US').map(state => ({ value: state.isoCode, label: state.name }))
    const [mechanics, setMechanics] = useState([])
    const [mechanicsLoading, setMechanicsLoading] = useState(false)
    const [neededFields, setNeededFields] = useState({})
    const [problemData, setProblemData] = useState(
        {
            asset: id,
            summary: '',
            estimatedFixDate: null,
            actualFixDate: null,
            estimatedCost: null,
            actualCost: null,
            resolved: false,
            notes: '',
            readiness: false,
            city: '',
            state: '',
            address: '',
            businessName: '',
            redTag: false,
            redTagNotes: ''
        }
    )

    const [originalRedTagValue, setOriginalRedTagValue] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState({
        picture: [],
        video: [],
        quote_document: []
    })
    const [existingAttachments, setExistingAttachments] = useState([])
    const [attachmentErrors, setAttachmentErrors] = useState({})
    const [stagedDeletions, setStagedDeletions] = useState([])
    const [attachmentsUploading, setAttachmentsUploading] = useState(false)
    const [attachmentUploadProgress, setAttachmentUploadProgress] = useState(0)
    const pendingAttachmentsRef = useRef(pendingAttachments)
    pendingAttachmentsRef.current = pendingAttachments
    const savedProblemIdRef = useRef(null)
    const submittingRef = useRef(false)
    const statusMessageRef = useRef(null)

    useEffect(() => {
        return () => revokePendingAttachmentPreviews(pendingAttachmentsRef.current)
    }, [])

    function loadNeededFieldsForAsset(assetData) {
        const fields = assetData?.deviceSubType?.frontendFields
        if (!fields) {
            setNeededFields({})
            return
        }
        const fieldList = Array.isArray(fields)
            ? fields
            : String(fields).split(',').map((f) => f.trim()).filter(Boolean)
        const newNeededFields = {}
        for (const fieldName of fieldList) {
            newNeededFields[fieldName] = true
        }
        setNeededFields(newNeededFields)
    }

    function scrollStatusIntoView() {
        if (statusMessageRef.current?.scrollIntoView) {
            statusMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
    }

    async function readApiError(resp, fallback) {
        try {
            const err = await resp.json()
            if (typeof err === 'string' && err) {
                return err
            }
            if (Array.isArray(err) && err[0]) {
                return err[0]
            }
            if (typeof err?.detail === 'string' && err.detail) {
                return err.detail
            }
            if (Array.isArray(err?.non_field_errors) && err.non_field_errors[0]) {
                return err.non_field_errors[0]
            }
            const firstKey = Object.keys(err || {}).find(
                (key) => Array.isArray(err[key]) && err[key].length
            )
            if (firstKey) {
                return err[firstKey][0]
            }
        } catch {
            // keep fallback
        }
        return fallback
    }

    async function loadMechanics() {
        setMechanicsLoading(true)
        try {
            const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'mechanics/', {
                headers: { 'X-CSRFToken': getCSRFToken() },
                credentials: 'include'
            })
            if (!response.ok) {
                throw new Error(`Failed to fetch mechanics: ${response.statusText}`)
            }
            const mechanicsData = await response.json()
            setMechanics(mechanicsData)
        } catch (err) {
            setError("Failed to load mechanics: " + err.message)
        } finally {
            setMechanicsLoading(false)
        }
    }

    function handleChange(key, value) {
        const problemDataCopy = structuredClone(problemData)
        problemDataCopy[key] = value
        const fieldErrorsCopy = structuredClone(fieldErrors)
        fieldErrorsCopy[key] = false
        if (key === 'resolved') {
            fieldErrorsCopy['actualFixDate'] = false
        }
        
        setFieldErrors(fieldErrorsCopy)
        setProblemData(problemDataCopy)
    }

    function handleMultipleChanges(updates) {
        const problemDataCopy = structuredClone(problemData)
        const fieldErrorsCopy = structuredClone(fieldErrors)
        
        Object.entries(updates).forEach(([key, value]) => {
            problemDataCopy[key] = value
            fieldErrorsCopy[key] = false
        })
        
        setFieldErrors(fieldErrorsCopy)
        setProblemData(problemDataCopy)
    }

    function handleAddAttachmentFiles (attachmentType, fileList) {
        if (!fileList || fileList.length === 0) {
            return
        }
        setPendingAttachments(current => ({
            ...current,
            [attachmentType]: [
                ...current[attachmentType],
                ...Array.from(fileList).map(file => createPendingAttachmentItem(attachmentType, file))
            ]
        }))
        setAttachmentErrors(current => ({ ...current, [attachmentType]: false }))
    }

    function handleRemovePendingAttachment (attachmentType, index) {
        setPendingAttachments(current => {
            const removed = current[attachmentType][index]
            if (removed?.previewUrl) {
                URL.revokeObjectURL(removed.previewUrl)
            }
            const updated = [...current[attachmentType]]
            updated.splice(index, 1)
            return { ...current, [attachmentType]: updated }
        })
    }

    function deleteExistingAttachment (attachmentId) {
        setStagedDeletions(current => [...current, attachmentId])
        setExistingAttachments(current => current.filter(attachment => attachment.id !== attachmentId))
    }

    async function flushStagedDeletions (currentProblemId) {
        if (stagedDeletions.length === 0) {
            return true
        }
        let hasFailures = false
        for (const attachmentId of stagedDeletions) {
            try {
                const resp = await deleteAttachment(id, currentProblemId, attachmentId)
                if (!resp.ok) {
                    hasFailures = true
                }
            } catch {
                hasFailures = true
            }
        }
        setStagedDeletions([])
        if (hasFailures) {
            return false
        }
        return true
    }

    function handleCancel () {
        revokePendingAttachmentPreviews(pendingAttachments)
        window.location.reload()
    }

    async function uploadPendingAttachments (currentProblemId) {
        setAttachmentsUploading(true)
        setAttachmentUploadProgress(0)

        let result
        try {
            result = await uploadPendingProblemAttachments(id, currentProblemId, pendingAttachments, {
                onProgress: setAttachmentUploadProgress
            })
        } finally {
            setAttachmentsUploading(false)
        }

        if (result.ok && result.uploaded.length === 0) {
            return true
        }
        if (!result.ok) {
            const uploadErrorMessage = 'Some files could not be uploaded. Check file type and size, then save again.';
            const failedTypeErrors = (result.failedTypes || []).reduce((errors, type) => {
                errors[type] = uploadErrorMessage;
                return errors;
            }, {});
            setAttachmentErrors(failedTypeErrors);
            return false
        }

        setExistingAttachments(current => [...current, ...result.uploaded])
        revokePendingAttachmentPreviews(pendingAttachments)
        setPendingAttachments({ picture: [], video: [], quote_document: [] })
        return true
    }

    function validateForm() {
        const isRedTagIssue = problemData.summary === 'dotViolation' || problemData.summary === 'safetyIssue'
        
        const requiredFields = isRedTagIssue 
            ? ['summary', 'notes'] 
            : ['summary', 'estimatedCost', 'estimatedFixDate', "notes", "city", "state", "address", "businessName"]
        
        if (problemData.resolved) {
            requiredFields.push('actualFixDate', 'actualCost', 'resolution')
        }
        let valid = true
        const aggErrors = {}
        
        if (isRedTagIssue && (!problemData.notes || problemData.notes.trim().length === 0)) {
            valid = false
            aggErrors['notes'] = "Comments are required for DOT Violation and Safety Issue."
        }
        
        for (const field of requiredFields) {
            if ((field === 'estimatedCost' || field === 'actualCost') && problemData[field] <= 0 && problemData['summary'] !== "warranty") {
                valid = false
                aggErrors[field] = "This field is required and must be greater than 0."
            } else if (problemData[field] === null || problemData[field].length === 0) {
                valid = false
                aggErrors[field] = "This field is required."
            }

            if (field === 'state') {
                const isValidState = states.some(state => state.label === problemData[field])
                if (!isValidState) {
                    valid = false
                    aggErrors[field] = "This field is required and must be a valid USA state"
                }
            }

            if (field === 'city') {
                const cities = City.getCitiesOfCountry("US").map(state => ({ value: state.isoCode, label: state.name }))
                const isValid = cities.some(city => city.label === problemData[field])
                if (!isValid) {
                    valid = false
                    aggErrors[field] = "This field is required and must be a real USA city"
                }
            }
        }

        // Notes required when unchecking the red tag
        if (originalRedTagValue === true && problemData.redTag === false) {
            if (!problemData.redTagNotes || problemData.redTagNotes.trim().length < 10) {
                valid = false
                aggErrors['redTag'] = 'Red Tag Notes are required to uncheck Red Tag. Please, provide meaningful information (at least 10 characters).'
            }
        }

        setFieldErrors(aggErrors)
        return valid
    }

    async function submitProblem() {
        if (submittingRef.current || !validateForm()) {
            return
        }
        submittingRef.current = true
        setSuccess(false)
        setError('')
        setLoaded(false)
        const problemDataClone = structuredClone(problemData)
        problemDataClone['actualCost'] = problemDataClone['actualCost'] || null

        // Remove mechanic field if user is not maintenance team
        if (!auth.userIsMaintenanceTeam && !auth.allowEditsAll) {
            delete problemDataClone['mechanic']
        }

        const currentProblemId = savedProblemIdRef.current || id2
        const isCreating = isNew && !savedProblemIdRef.current

        try {
            if (isCreating) {
                problemDataClone['estimatedFixDate'] = problemDataClone['estimatedFixDate'] ? problemDataClone['estimatedFixDate'].toISOString().substring(0, 10) : null
                problemDataClone['actualFixDate'] = problemDataClone['actualFixDate'] ? problemDataClone['actualFixDate'].toISOString().substring(0, 10) : null
                problemDataClone['asset'] = id

                const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'assets/' + id + '/problems/', {
                    method: 'POST',
                    headers: { 'X-CSRFToken': getCSRFToken(), 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(problemDataClone)
                })
                if (!resp.ok) {
                    setError(await readApiError(resp, 'Something went wrong'))
                    scrollStatusIntoView()
                    return
                }
                const respJson = await resp.json()
                const newId = respJson['id']
                // Mark created before UI unlocks so a second Save cannot POST again.
                savedProblemIdRef.current = newId
                window.history.replaceState(null, "", "/assets/" + id + "/problems/" + newId)

                const deletionsSucceeded = await flushStagedDeletions(newId)
                const uploadsSucceeded = await uploadPendingAttachments(newId)
                let attachmentError = ''
                if (!deletionsSucceeded && !uploadsSucceeded) {
                    attachmentError = 'Problem created, but some attachments could not be deleted and some failed to upload.'
                } else if (!deletionsSucceeded) {
                    attachmentError = 'Problem created, but some attachments could not be deleted.'
                } else if (!uploadsSucceeded) {
                    attachmentError = 'Problem created, but one or more attachments failed to upload. Please fix the files and save again.'
                }

                respJson['estimatedFixDate'] = respJson['estimatedFixDate'] ? new Date(respJson['estimatedFixDate']) : null
                respJson['actualFixDate'] = respJson['actualFixDate'] ? new Date(respJson['actualFixDate']) : null
                setProblemData(respJson)
                await fetchAttachments(newId)
                if (attachmentError) {
                    setError(attachmentError)
                } else {
                    setSuccess(true)
                }
                scrollStatusIntoView()
            } else {
                problemDataClone['estimatedFixDate'] = problemDataClone['estimatedFixDate'] ? problemDataClone['estimatedFixDate'].toISOString().substring(0, 10) : null
                problemDataClone['actualFixDate'] = problemDataClone['actualFixDate'] ? problemDataClone['actualFixDate'].toISOString().substring(0, 10) : null

                const deletionsSucceeded = await flushStagedDeletions(currentProblemId)
                const uploadsSucceeded = await uploadPendingAttachments(currentProblemId)
                if (!deletionsSucceeded || !uploadsSucceeded) {
                    if (!deletionsSucceeded && !uploadsSucceeded) {
                        setError('Problem was not updated because deleting and uploading attachments failed. Please resolve attachment errors and save again.')
                    } else if (!deletionsSucceeded) {
                        setError('Problem was not updated because some attachments could not be deleted. Please try saving again.')
                    } else {
                        setError('Problem was not updated because one or more attachments failed to upload. Please fix the files and save again.')
                    }
                    scrollStatusIntoView()
                    return
                }

                const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'assets/' + id + '/problems/' + currentProblemId + '/', {
                    method: 'PATCH',
                    headers: { 'X-CSRFToken': getCSRFToken(), 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(problemDataClone)
                })
                if (!resp.ok) {
                    setError(await readApiError(
                        resp,
                        'Something went wrong. Perhaps this team does not already have a readiness report to update?'
                    ))
                    scrollStatusIntoView()
                    return
                }
                const respJson = await resp.json()
                respJson['estimatedFixDate'] = respJson['estimatedFixDate'] ? new Date(respJson['estimatedFixDate']) : null
                respJson['actualFixDate'] = respJson['actualFixDate'] ? new Date(respJson['actualFixDate']) : null
                setProblemData(respJson)
                await fetchAttachments(currentProblemId)
                setSuccess(true)
                scrollStatusIntoView()
            }
        } finally {
            setLoaded(true)
            submittingRef.current = false
        }
    }

    async function fetchAttachments(problemId) {
        try {
            const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'assets/' + id + '/problems/' + problemId + '/', {
                method: 'GET',
                headers: { 'X-CSRFToken': getCSRFToken(), 'Content-Type': 'application/json' },
                credentials: 'include'
            })
            if (resp.ok) {
                const data = await resp.json()
                setExistingAttachments(data.attachments || [])
            }
        } catch {
            console.error('Failed to fetch attachments for problem', problemId)
        }
    }

    async function getReport() {
        setSuccess(false)
        setError('')
        setLoaded(false)
        const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'assets/' + id + '/problems/' + id2 + '/', {
            method: 'GET',
            headers: { 'X-CSRFToken': getCSRFToken(), 'Content-Type': 'application/json' },
            credentials: 'include'
        })
        setLoaded(true)
        if (!resp.ok) {
            setError("Something went wrong")
            return
        }
        const respJson = await resp.json()
        respJson['estimatedFixDate'] = respJson['estimatedFixDate'] ? new Date(respJson['estimatedFixDate']) : null
        respJson['actualFixDate'] = respJson['actualFixDate'] ? new Date(respJson['actualFixDate']) : null

        respJson['redTag'] = respJson['redTag'] || false
        respJson['redTagNotes'] = respJson['redTagNotes'] || ''

        // Store the original database value
        const originalRedTag = respJson['redTag']
        setOriginalRedTagValue(originalRedTag)
        setProblemData(respJson)
        setExistingAttachments(respJson.attachments || [])
        setStagedDeletions([])
        setPendingAttachments({ picture: [], video: [], quote_document: [] })
    }

    useEffect(() => {
        getAsset(id).then((value) => {
            setAsset(value)
            loadNeededFieldsForAsset(value)
        })
        getUnresolvedProblems(id).then((value) => {
            setUnresolvedProblems(value)
        })
        loadMechanics()
        savedProblemIdRef.current = null
        if (!isNew) {
            getReport()
        } else {
            setOriginalRedTagValue(false)
            setPendingAttachments({ picture: [], video: [], quote_document: [] })
            setExistingAttachments([])
            setStagedDeletions([])
            setAttachmentErrors({})
            setProblemData({
                summary: '',
                estimatedFixDate: null,
                actualFixDate: null,
                estimatedCost: '',
                actualCost: '',
                modifiedTime: null,
                resolved: false,
                notes: '',
                resolution: '',
                readiness: false,
                city: '',
                state: '',
                address: '',
                businessName: '',
                redTag: false,
                redTagNotes: ''
            })
        }
    }, [id2])

    const showMakeModelYear = neededFields.make || neededFields.model || neededFields.year
    const showVin = neededFields.vin

    if (unresolvedProblems.length && isNew) {
        return <div>
            There is already an <Link to={'../' + unresolvedProblems[0].id}>open problem</Link>. Please resolve that one first.
        </div>
    }

    // Format mechanics for dropdown
    const mechanicOptions = (mechanics || []).map(mechanic => ({
        key: mechanic.userId,
        value: mechanic.userId,
        text: mechanic.name,
        content: (
            <div>
                <strong>{mechanic.name}</strong>
                <div style={{ fontSize: '0.9em', color: 'gray' }}>ID: {mechanic.userId}</div>
            </div>
        )
    }));

    // Format states for dropdown
    const stateOptions = states.map(state => ({
        key: state.value,
        value: state.label,
        text: `${state.label} (${state.value})`
    }));

    return <LoadingWrapper
        loading={!loaded || mechanicsLoading || attachmentsUploading}
        pageDimmer
        text={attachmentsUploading ? 'Uploading attachments...' : undefined}
        loadingContent={attachmentsUploading
            ? (
                <Progress
                    percent={attachmentUploadProgress}
                    indicating
                    progress
                    size="small"
                />
            )
            : null}
    ><div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <div id="asset-form-wrapper" style={{ minWidth: "15rem", maxWidth: "40rem", display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div
                ref={statusMessageRef}
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 20,
                    width: '100%',
                    background: '#fff',
                    paddingTop: '0.5rem',
                    paddingBottom: '0.5rem',
                }}
            >
                {success && <Message positive style={{ width: '100%' }}>Success</Message>}
                {error && <Message negative style={{ width: '100%' }}>{error}</Message>}
            </div>
            <Form style={{ marginBottom: '2rem', width: "100%" }}>
                <FormGroup>
                    <FormField width={8}>
                        <label>Asset name <Popup on="hover" content="Filled in automatically." trigger={<Icon className="info circle" />} /></label>
                        <Form.Input className="icct-disabled-input" value={asset.iccName} loading={!asset.iccName} />
                    </FormField>
                    <FormField width={8}>
                        <label>Last modified <Popup on="hover" content="Filled in automatically." trigger={<Icon className="info circle" />} /></label>
                        <Form.Input className="icct-disabled-input" value={problemData.modifiedTime ? new Date(problemData.modifiedTime).toLocaleDateString() : ""} />
                    </FormField>
                </FormGroup>
                {(showMakeModelYear || showVin) && (
                    <>
                        {showMakeModelYear && (
                            <FormGroup>
                                {neededFields.make && (
                                    <FormField width={4}>
                                        <label>Make <Popup on="hover" content="From asset record." trigger={<Icon className="info circle" />} /></label>
                                        <Form.Input className="icct-disabled-input" value={asset.make || ''} readOnly />
                                    </FormField>
                                )}
                                {neededFields.model && (
                                    <FormField width={4}>
                                        <label>Model <Popup on="hover" content="From asset record." trigger={<Icon className="info circle" />} /></label>
                                        <Form.Input className="icct-disabled-input" value={asset.model || ''} readOnly />
                                    </FormField>
                                )}
                                {neededFields.year && (
                                    <FormField width={4}>
                                        <label>Year <Popup on="hover" content="From asset record." trigger={<Icon className="info circle" />} /></label>
                                        <Form.Input className="icct-disabled-input" value={asset.year ?? ''} readOnly />
                                    </FormField>
                                )}
                            </FormGroup>
                        )}
                        {showVin && (
                            <FormGroup>
                                <FormField width={8}>
                                    <label>VIN <Popup on="hover" content="From asset record." trigger={<Icon className="info circle" />} /></label>
                                    <Form.Input className="icct-disabled-input" value={asset.vin || ''} readOnly />
                                </FormField>
                            </FormGroup>
                        )}
                        <div className='divider-bottom' /><div />
                    </>
                )}
                <FormGroup>
                    <FormField width={16} required >
                        <label>Issue summary</label>
                        <Form.Select
                            options={[
                                { key: 'accident', text: 'Accident', value: 'accident' },
                                { key: 'mechanicalFailure', text: 'Mechanical Failure', value: 'mechanicalFailure' },
                                { key: 'routineMaintenance', text: 'Routine Maintenance', value: 'routineMaintenance' },
                                { key: 'warranty', text: 'Warranty/Recall', value: 'warranty' },
                                { key: 'dotViolation', text: 'DOT Violation', value: 'dotViolation' },
                                { key: 'safetyIssue', text: 'Safety Issue', value: 'safetyIssue' }
                            ]}
                            value={problemData.summary}
                            onChange={(e, { value }) => {
                                const updates = { summary: value }
                                if (value === 'dotViolation' || value === 'safetyIssue') {
                                    updates.redTag = true
                                }
                                handleMultipleChanges(updates)
                            }}
                            error={fieldErrors.summary}
                        />
                    </FormField>
                </FormGroup>
                {(auth.userIsMaintenanceTeam || problemData.mechanic || auth.allowEditsAll) && (
                    <FormGroup>
                        <FormField width={16} >
                            <label>Mechanic</label>
                            <Form.Select
                                placeholder="Select a mechanic..."
                                clearable
                                disabled={!auth.userIsMaintenanceTeam && !auth.allowEditsAll}
                                options={mechanicOptions}
                                value={problemData.mechanic}
                                onChange={(e, value) => handleChange('mechanic', value.value)}
                                error={fieldErrors.mechanic}
                            />
                        </FormField>
                    </FormGroup>
                )}
                <FormGroup>
                    <FormField width={8} required>
                        <label>Estimated cost</label>
                        <Form.Input
                            error={fieldErrors.estimatedCost}
                            icon="dollar"
                            onChange={(e, value) => handleChange('estimatedCost', value.value)}
                            value={problemData.estimatedCost}
                            onKeyPress={(event) => {
                                if (!(/[0-9]/.test(event.key) || /./.test(event.key))) {
                                    event.preventDefault();
                                }
                            }} />
                    </FormField>
                    <FormField width={8} required={problemData.resolved}>
                        <label>Actual cost</label>
                        <Form.Input
                            error={fieldErrors.actualCost}
                            icon="dollar"
                            onChange={(e, value) => handleChange('actualCost', value.value)}
                            value={problemData.actualCost}
                            onKeyPress={(event) => {
                                if (!(/[0-9]/.test(event.key) || /./.test(event.key))) {
                                    event.preventDefault();
                                }
                            }} />
                    </FormField>
                </FormGroup>
                <FormGroup>
                    <FormField required width={8}>
                        <label>Estimated fix date</label>
                        <IcctDatePicker
                            value={problemData.estimatedFixDate}
                            keyName="estimatedFixDate"
                            onChange={handleChange}
                            error={fieldErrors.estimatedFixDate} />
                    </FormField>
                    <FormField required={problemData.resolved} width={8}>
                        <label>Actual fix date</label>
                        <IcctDatePicker
                            value={problemData.actualFixDate}
                            keyName="actualFixDate"
                            onChange={handleChange}
                            error={fieldErrors.actualFixDate} />
                    </FormField>
                </FormGroup>
                <div className='divider-bottom' /><div />
                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                    <strong>Asset Location</strong>
                </div>
                <FormGroup>
                    <FormField width={16} required>
                        <label>Business Name</label>
                        <Form.Input
                            error={fieldErrors.businessName}
                            onChange={(e, value) => handleChange('businessName', value.value)}
                            value={problemData.businessName} />
                    </FormField>
                    <FormField width={16} required>
                        <label>Address</label>
                        <Form.Input
                            error={fieldErrors.address}
                            onChange={(e, value) => handleChange('address', value.value)}
                            value={problemData.address} />
                    </FormField>
                </FormGroup>
                <FormGroup>
                    <FormField width={16} required>
                        <label>City</label>
                        <Form.Input
                            error={fieldErrors.city}
                            onChange={(e, value) => handleChange('city', value.value)}
                            value={problemData.city} />
                    </FormField>
                    <FormField width={16} required>
                        <label>State</label>
                        <Form.Select
                            placeholder="Select a state..."
                            search
                            options={stateOptions}
                            error={fieldErrors.state}
                            onChange={(e, value) => handleChange('state', value.value)}
                            value={problemData.state} />
                    </FormField>
                </FormGroup>
                <div className='divider-bottom' /><div />
                <FormGroup>
                    <FormField width={16}>
                        <label>Resolved?</label>
                        <Checkbox
                            toggle
                            checked={problemData.resolved}
                            value={problemData.resolved}
                            onChange={(e, value) => handleChange('resolved', value.checked)}
                        />
                    </FormField>
                </FormGroup>
                <FormField required>
                    <label>Notes</label>
                    <TextArea value={problemData.notes} onChange={(e, value) => handleChange('notes', value.value)}
                    />
                    {fieldErrors?.notes && <p style={{ color: 'red' }}>{fieldErrors.notes}</p>}
                </FormField>
                <FormField required={problemData.resolved}>
                    <label>Resolution</label>
                    <TextArea value={problemData.resolution} onChange={(e, value) => handleChange('resolution', value.value)}
                    />
                    {fieldErrors?.resolution && <p style={{ color: 'red' }}>{fieldErrors.resolution}</p>}
                </FormField>

                <div className='divider-bottom' /><div />
                <>
                <div style={{ textAlign: "center", marginBottom: "20px" }}>
                    <strong>Red Tag Process</strong>
                </div>
                <FormGroup>
                    <FormField width={16}>
                        <label htmlFor="redTagCheckbox">Red Tag Issue?</label>
                        <Checkbox
                                    id="redTagCheckbox"
                                    toggle
                                    checked={problemData.redTag}
                                    onChange={(e, data) => handleChange('redTag', data.checked)}
                                />
                    </FormField>
                </FormGroup>

                <FormField required={originalRedTagValue === true && problemData.redTag === false}>
                    <label>Red Tag Notes</label>
                    <TextArea
                        value={problemData.redTagNotes}
                        onChange={(e, data) => handleChange('redTagNotes', data.value)}
                        placeholder={
                            originalRedTagValue === true && problemData.redTag === false
                                ? "Required: Please, provide Red Tag Resolution Notes."
                                : problemData.redTag
                                    ? "Optional: Provide additional details about the Red Tag."
                                    : ""
                        }
                    />
                </FormField>
                </>

                {fieldErrors?.redTag && <Message negative>{fieldErrors.redTag}</Message>}

                <div className='divider-bottom' /><div />

                {
                    <>
                        <AssetProblemAttachments
                            pendingAttachments={pendingAttachments}
                            existingAttachments={existingAttachments}
                            attachmentErrors={attachmentErrors}
                            onAddFiles={handleAddAttachmentFiles}
                            onRemovePending={handleRemovePendingAttachment}
                            onDeleteExisting={deleteExistingAttachment}
                        />

                        <div className='divider-bottom' /><div />
                    </>
                }

                {!problemData.resolved && asset.associatedTeam && <FormField>
                    <label>
                        Automatically update {asset.associatedTeam ? <Link to={'/teams/' + asset.associatedTeam?.id + '/view'}>{asset.associatedTeam?.name}</Link> : <Loader active inline size='tiny' />}
                        's readiness to no longer ready?
                        <Popup
                            trigger={<Icon className="info circle" style={{ paddingLeft: "0.3rem" }} />}
                            on="hover"
                            content={"Please note that a readiness report will be submitted regardless, with the asset as \"Out of Service\", and that the asset status will be updated."} />
                    </label>
                    <Checkbox
                        toggle
                        checked={problemData.readiness}
                        value={problemData.readiness}
                        onChange={(e, value) => handleChange('readiness', value.checked)}
                    />
                </FormField>}
            </Form>
            {(success || error) && (
                <div style={{ width: '100%', marginBottom: '1rem' }}>
                    {success && <Message positive style={{ width: '100%' }}>Success</Message>}
                    {error && <Message negative style={{ width: '100%' }}>{error}</Message>}
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                {!isReadOnly && (
                    <Button
                        style={{ backgroundColor: "var(--add)", color: 'white' }}
                        onClick={submitProblem}
                        disabled={!loaded || attachmentsUploading}
                    >
                        Save
                    </Button>
                )}
                <Button onClick={handleCancel}>Cancel</Button>
            </div>
        </div>

    </div> </LoadingWrapper>
}
